import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, setActingFor } from '../api/client';
import { token as liveToken } from '../auth/session';
import { useT } from '../i18n/i18n';
import { formatMb } from '../media/media';
import { NEED_LABEL, describe, hours, money, openFile } from './useAssistAccess';
import type { Work } from './ExpertsPage';
import DateField from '../components/DateField';

type Profile = {
  name: string; headline: string; bio: string; specialisms: string[]; qualifications: string;
  registration: string; dbsNumber: string | null; dbsIssued: string | null; dateOfBirth: string | null;
  dbsConsent: boolean; agreementVersion: string | null; hourlyPence: number;
};
type Mine = {
  status: string | null; decisionNote: string | null; profile: Profile | null;
  dbsVerified: boolean; dbsCheckedOn: string | null; reply: string | null; canReply: boolean; consultant: boolean;
};
type Doc = { id: string; kind: string; filename: string; contentType: string; sizeBytes: number; uploadedAt: string };

const AGREEMENT_VERSION = '2026-10-21';
const STATUS: Record<string, string> = {
  APPLIED: 'Your application is being checked. You will be listed once it is approved.',
  APPROVED: 'You are approved and listed for families to choose.',
  REJECTED: 'Your application was not approved.',
  SUSPENDED: 'Your listing is paused.',
  REMOVED: 'You are no longer listed on Assist.',
};
const KIND: Record<string, string> = {
  QUALIFICATION: 'Qualification certificate',
  REGISTRATION: 'Registration evidence',
  INSURANCE: 'Insurance',
  OTHER: 'Other',
};
const CAN_REPLY = ['REJECTED', 'SUSPENDED', 'REMOVED'];
const WORK_STATUS: Record<string, string> = {
  REQUESTED: 'Needs your estimate', QUOTED: 'Estimate sent', ACTIVE: 'Working', ENDED: 'Ended', DECLINED: 'Declined',
};
const ESTIMATES = [15, 30, 45, 60, 90, 120, 150, 180, 240, 300, 360, 480, 600, 720, 960, 1200];
const EMPTY: Profile = {
  name: '', headline: '', bio: '', specialisms: [], qualifications: '', registration: '',
  dbsNumber: null, dbsIssued: null, dateOfBirth: null, dbsConsent: false, agreementVersion: null, hourlyPence: 4000,
};

function ProfileForm({ initial, onSaved }: { initial: Profile; onSaved: () => void }) {
  const t = useT();
  const [p, setP] = useState<Profile>(initial);
  const [spec, setSpec] = useState(initial.specialisms.join(', '));
  const [agreed, setAgreed] = useState(initial.agreementVersion === AGREEMENT_VERSION);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Profile>(k: K, v: Profile[K]) => setP((x) => ({ ...x, [k]: v }));

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api('/assist/me/expert', {
        method: 'POST', token: true,
        body: { ...p, specialisms: spec.split(',').map((s) => s.trim()).filter(Boolean), agreementVersion: agreed ? AGREEMENT_VERSION : null },
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="assist-form">
      <div className="field"><label htmlFor="ex-name">{t('Your name as families will see it')}</label><input id="ex-name" maxLength={80} value={p.name} onChange={(e) => set('name', e.target.value)} /></div>
      <div className="field"><label htmlFor="ex-head">{t('One line about you')}</label><input id="ex-head" maxLength={140} value={p.headline} placeholder={t('e.g. Specialist speech and language therapist, 12 years with autistic children')} onChange={(e) => set('headline', e.target.value)} /></div>
      <div className="field"><label htmlFor="ex-bio">{t('About your work')}</label><textarea id="ex-bio" rows={5} maxLength={3000} value={p.bio} onChange={(e) => set('bio', e.target.value)} /></div>
      <div className="field"><label htmlFor="ex-spec">{t('What you specialise in')}</label><input id="ex-spec" value={spec} placeholder={t('e.g. AAC, early communication, ABA, sensory, teenagers')} onChange={(e) => setSpec(e.target.value)} /><p className="field-hint">{t('Separate them with commas.')}</p></div>
      <div className="field"><label htmlFor="ex-qual">{t('Qualifications')}</label><textarea id="ex-qual" rows={4} maxLength={3000} value={p.qualifications} onChange={(e) => set('qualifications', e.target.value)} /></div>
      <div className="field"><label htmlFor="ex-reg">{t('Professional registration')}</label><input id="ex-reg" maxLength={200} value={p.registration} placeholder={t('e.g. HCPC SL12345, Teaching Regulation Agency, UKBA')} onChange={(e) => set('registration', e.target.value)} /></div>
      <h3 style={{ margin: 0 }}>{t('DBS check')}</h3>
      <p className="field-hint" style={{ margin: 0 }}>{t('Your enhanced DBS certificate must be on the DBS Update Service. EasyLearning checks it online and records only the date it was checked; we do not keep your certificate.')}</p>
      <div className="field"><label htmlFor="ex-dbs-no">{t('DBS certificate number')}</label><input id="ex-dbs-no" inputMode="numeric" maxLength={12} value={p.dbsNumber ?? ''} onChange={(e) => set('dbsNumber', e.target.value.replace(/\D/g, '').slice(0, 12) || null)} /><p className="field-hint">{t('The 12 digits on your certificate.')}</p></div>
      <div className="field"><label htmlFor="ex-dbs-issued">{t('Date issued')}</label><DateField id="ex-dbs-issued" value={p.dbsIssued ?? ''} onChange={(v) => set('dbsIssued', v)} /></div>
      <div className="field"><label htmlFor="ex-dob">{t('Your date of birth')}</label><DateField id="ex-dob" value={p.dateOfBirth ?? ''} onChange={(v) => set('dateOfBirth', v)} /></div>
      <label className="studio-photo-check"><input type="checkbox" checked={p.dbsConsent} onChange={(e) => set('dbsConsent', e.target.checked)} /><span>{t('I subscribe to the DBS Update Service and agree that EasyLearning may check my certificate.')}</span></label>
      <div className="field"><label htmlFor="ex-rate">{t('Your hourly rate (£)')}</label><input id="ex-rate" type="number" min={10} max={500} step={1} value={p.hourlyPence / 100} onChange={(e) => set('hourlyPence', Math.round(Number(e.target.value) * 100))} /><p className="field-hint">{t("Families pay this plus EasyLearning's fee. You are paid this rate for the time you log.")}</p></div>
      <label className="studio-photo-check"><input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} /><span>{t('I have read and accept the consultant agreement')}</span></label>
      <p className="field-hint" style={{ margin: 0 }}><a href="/assist/consultant-agreement" target="_blank" rel="noopener noreferrer">{t('Read the consultant agreement')}</a></p>
      <p className="field-hint">{t('Changing your name, qualifications, registration or DBS details sends your profile back to be checked.')}</p>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions"><button type="button" className="btn" disabled={busy || !p.dbsConsent || !agreed} onClick={() => void save()}>{t('Save')}</button></div>
    </div>
  );
}

function Documents() {
  const t = useT();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [kind, setKind] = useState('QUALIFICATION');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void api<Doc[]>('/assist/me/expert/documents', { token: true }).then(setDocs).catch(() => setDocs([]));
  }, []);

  async function upload() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file, file.name);
      const bearer = await liveToken();
      const res = await fetch('/api/assist/me/expert/documents', {
        method: 'POST',
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
        body: form,
        credentials: 'omit',
      });
      if (!res.ok) {
        let message = t('Could not save ({status})', { status: res.status });
        try { message = (await res.json()).message ?? message; } catch { message = t('Could not save ({status})', { status: res.status }); }
        throw new Error(message);
      }
      setDocs(await res.json());
      setFile(null);
      if (input.current) input.current.value = '';
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(d: Doc) {
    if (!window.confirm(t('Remove {file}?', { file: d.filename }))) return;
    setError(null);
    try {
      setDocs(await api<Doc[]>(`/assist/me/expert/documents/${d.id}`, { method: 'DELETE', token: true }));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be done.'));
    }
  }

  function open(d: Doc) {
    setError(null);
    void openFile(`/assist/me/expert/documents/${d.id}`).catch((e) => setError(e instanceof Error ? e.message : t('That file could not be opened.')));
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>{t('Documents')}</h2>
      <p>{t('Add your qualification certificates, evidence of your professional registration and your insurance. EasyLearning checks them before you are listed. Families do not see them.')}</p>
      {docs.length === 0 ? <p className="muted">{t('No documents yet.')}</p> : (
        <div className="list">
          {docs.map((d) => (
            <div key={d.id} className="list-item">
              <div className="list-item-text">
                <div className="name">{d.filename}</div>
                <div className="list-item-meta">{KIND[d.kind] ? t(KIND[d.kind]) : d.kind} · {formatMb(d.sizeBytes)} · {new Date(d.uploadedAt).toLocaleDateString()}</div>
              </div>
              <div className="toolbar">
                <button type="button" className="btn secondary" onClick={() => open(d)}>{t('Open')}</button>
                <button type="button" className="btn quiet-danger" onClick={() => void remove(d)}>{t('Remove')}</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="assist-form">
        <div className="field">
          <label htmlFor="doc-kind">{t('What it is')}</label>
          <select id="doc-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(KIND).map(([k, label]) => <option key={k} value={k}>{t(label)}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="doc-file">{t('File')}</label>
          <input id="doc-file" ref={input} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <p className="field-hint">{t('PDF, JPG or PNG, up to 5 MB each. You can add up to 12.')}</p>
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="form-actions"><button type="button" className="btn" disabled={busy || !file} onClick={() => void upload()}>{t('Upload')}</button></div>
      </div>
    </div>
  );
}

function ReplyBox({ onSent }: { onSent: () => void }) {
  const t = useT();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await api('/assist/me/expert/reply', { method: 'POST', token: true, body: { text } });
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved.'));
      setBusy(false);
    }
  }

  return (
    <div className="assist-form">
      <div className="field">
        <label htmlFor="ex-reply">{t('Send a reply')}</label>
        <textarea id="ex-reply" rows={4} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
        <p className="field-hint">{t('You can send one reply to this decision. EasyLearning reads every reply.')}</p>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions"><button type="button" className="btn" disabled={busy || !text.trim()} onClick={() => void send()}>{t('Send reply')}</button></div>
    </div>
  );
}

function WorkItem({ w, onChange }: { w: Work; onChange: () => void }) {
  const t = useT();
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState('');
  const [estimate, setEstimate] = useState(w.quoteMinutes ?? 120);
  const [plan, setPlan] = useState(w.quoteNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const left = w.minutesBought - w.minutesUsed;

  async function run(path: string, body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      await api(path, { method: 'POST', token: true, body });
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved.'));
    } finally {
      setBusy(false);
    }
  }

  function open() {
    setActingFor({ engagementId: w.id, family: w.familyName || w.familyEmail || t('this family'), learners: w.learners.map((l) => l.name) });
    window.location.assign('/');
  }

  function decline() {
    const why = window.prompt(t('Tell the family why, so they can choose someone else.'));
    if (why === null) return;
    void run(`/assist/work/${w.id}/decline`, { note: why });
  }

  function finish() {
    if (!window.confirm(t("Mark this work as finished? You will no longer be able to see or change this family's learners, and the time you have not logged is refunded to them."))) return;
    void run(`/assist/work/${w.id}/finish`);
  }

  return (
    <article className="assist-work">
      <div className="studio-type-head">
        <h3 style={{ margin: 0 }}>{w.familyName || w.familyEmail}</h3>
        <span className="pill">{WORK_STATUS[w.status] ? t(WORK_STATUS[w.status]) : w.status}</span>
      </div>
      <p className="muted" style={{ margin: 0 }}>{w.familyEmail} · {t('for {learners}', { learners: w.learners.map((l) => l.name).join(` ${t('and')} `) })} · {t('{price} an hour to you', { price: money(w.hourPence) })}</p>
      {w.needs.length > 0 && <p style={{ margin: 0 }}>{t('They need: {needs}', { needs: w.needs.map((n) => (NEED_LABEL[n] ? t(NEED_LABEL[n]) : n)).join(', ') })}</p>}
      {w.request && <p className="ai-text">{w.request}</p>}

      {(w.status === 'REQUESTED' || w.status === 'QUOTED') && (
        <div className="assist-form">
          {w.status === 'QUOTED' && w.quoteMinutes != null && <p style={{ margin: 0 }}>{t('You estimated {time}. The family has not accepted it yet; you can change it.', { time: describe(w.quoteMinutes) })}</p>}
          <div className="field">
            <label htmlFor={`est-${w.id}`}>{t('How long it will take')}</label>
            <select id={`est-${w.id}`} value={estimate} onChange={(e) => setEstimate(Number(e.target.value))}>
              {ESTIMATES.map((m) => <option key={m} value={m}>{t('{time}, {price} to you', { time: describe(m), price: money(Math.round(w.hourPence * m / 60)) })}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`plan-${w.id}`}>{t('What you will do')}</label>
            <textarea id={`plan-${w.id}`} rows={4} maxLength={2000} value={plan} onChange={(e) => setPlan(e.target.value)}
              placeholder={t('e.g. 1 hour to set up programs for colours and shapes, 1 hour for a weekly plan with milestones for the next 6 weeks.')} />
            <p className="field-hint">{t("The family sees this with the price, including EasyLearning's fee. You can only log the time they pay for.")}</p>
          </div>
          <div className="toolbar">
            <button type="button" className="btn" disabled={busy} onClick={() => void run(`/assist/work/${w.id}/quote`, { minutes: estimate, note: plan })}>{w.status === 'QUOTED' ? t('Change estimate') : t('Send estimate')}</button>
            <button type="button" className="btn quiet-danger" disabled={busy} onClick={decline}>{t('Decline')}</button>
          </div>
        </div>
      )}

      {(w.status === 'ACTIVE' || w.status === 'ENDED') && <p style={{ margin: 0 }}>{t('{used} logged of {bought} paid for.', { used: hours(w.minutesUsed), bought: hours(w.minutesBought) })}</p>}
      {w.status === 'ACTIVE' && (
        <>
          <div className="form-actions"><button type="button" className="btn" onClick={open}>{t('Work on this')}</button></div>
          <div className="assist-time">
            <select aria-label={t('Time spent')} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {[15, 30, 45, 60, 90, 120, 180, 240].filter((m) => m <= left).map((m) => <option key={m} value={m}>{t('{m} minutes', { m })}</option>)}
            </select>
            <input aria-label={t('What you did')} maxLength={400} value={note} placeholder={t('What you did')} onChange={(e) => setNote(e.target.value)} />
            <button type="button" className="btn secondary" disabled={busy || left < 15} onClick={() => void run(`/assist/work/${w.id}/time`, { minutes, note }).then(() => setNote(''))}>{t('Log time')}</button>
            <button type="button" className="btn quiet-danger" onClick={finish}>{t('Finished')}</button>
          </div>
          {left < 15 && <p className="field-hint">{t('No paid time left. Ask the family to buy more hours before doing more.')}</p>}
        </>
      )}
      {error && <p className="error" role="alert">{error}</p>}
    </article>
  );
}

export default function ExpertWorkspace() {
  const t = useT();
  const [mine, setMine] = useState<Mine | null>(null);
  const [work, setWork] = useState<Work[]>([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    void api<Mine>('/assist/me/expert', { token: true }).then((m) => {
      setMine(m);
      if (m.consultant) void api<Work[]>('/assist/work', { token: true }).then(setWork).catch(() => setWork([]));
    }).catch((e) => setError(e instanceof Error ? e.message : t('Could not load.')));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="card"><p className="error">{error}</p></div>;
  if (!mine) return null;

  return (
    <div className="assist-home">
      <div className="card">
        <p className="field-hint" style={{ marginTop: 0 }}><Link to="/assist">{t('Assist')}</Link></p>
        <h1 style={{ marginTop: 0 }}>{t('Work as a consultant')}</h1>
        {mine.status === 'APPROVED' && !mine.consultant ? <p>{t('Your consultant access is turned off, so families cannot find you. Contact EasyLearning at support@fractalon.net.')}</p>
          : mine.status ? <p>{STATUS[mine.status] ? t(STATUS[mine.status]) : mine.status}{mine.decisionNote ? ` ${mine.decisionNote}` : ''}</p>
          : <p>{t('Families choose consultants to set up EasyLearning, plan courses and programs for their learners. Apply with your qualifications, registration and DBS details. EasyLearning checks every application before you are listed.')}</p>}
        <p className="field-hint">{t('You work for yourself, not for EasyLearning. Families who choose you send you a request, and you decide whether to take it. You set your own rate and decide how you do the work. EasyLearning checks you before you are listed, runs Assist and takes payment for you.')}</p>
        {mine.dbsVerified && mine.dbsCheckedOn && <p>{t('DBS checked by EasyLearning on {date}', { date: new Date(mine.dbsCheckedOn).toLocaleDateString() })}</p>}
        {mine.status && CAN_REPLY.includes(mine.status) && (mine.canReply
          ? <ReplyBox onSent={load} />
          : mine.reply && <><h3 style={{ marginBottom: 0 }}>{t('Your reply')}</h3><p className="ai-text">{mine.reply}</p></>)}
        {(!mine.status || editing)
          ? <ProfileForm initial={mine.profile ? { ...EMPTY, ...mine.profile } : EMPTY} onSaved={() => { setEditing(false); load(); }} />
          : <button type="button" className="btn secondary" onClick={() => setEditing(true)}>{t('Change your profile')}</button>}
      </div>
      {mine.status && <Documents />}
      {mine.consultant && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>{t('Families who asked you')}</h2>
          {work.length === 0 && <p className="muted">{t('No one yet.')}</p>}
          <div className="list">{work.map((w) => <WorkItem key={w.id} w={w} onChange={load} />)}</div>
        </div>
      )}
    </div>
  );
}
